import { defineStore } from 'pinia';
import { apiService, AppStructure } from '../services/apiService';
import { ChunkedUploadService, type ChunkedUploadOptions } from '../services/chunkedUploadService';
import type { FileItem, Folder, DownloadResponse, DownloadRequest, ApiError, UploadProgress } from '../types/api';

interface UploadingFile {
  id: string
  name: string
  size: number
  mimeType: string
  folderId: string | null
  appId: string
  uploadId: string
  progress: number
  status: 'uploading' | 'processing' | 'error'
  error?: string
  createdAt: string
}

interface FileState {
  files: FileItem[]
  folders: Folder[]
  uploadingFiles: UploadingFile[]
  currentFile: FileItem | null
  currentFolderId: string | null
  loading: boolean
  error: string | null
  uploadProgress: Record<string, UploadProgress>
  downloadProgress: Record<string, { loaded: number; total: number }>
}

export const useFileStore = defineStore('file', {
  state: (): FileState => ({
    files: [],
    folders: [],
    uploadingFiles: [],
    currentFile: null,
    currentFolderId: null,
    loading: false,
    error: null,
    uploadProgress: {},
    downloadProgress: {}
  }),

  getters: {
    getFileById: (state) => (id: string): FileItem | undefined => {
      return state.files.find((file: FileItem) => file.id === id);
    },
    getFolderById: (state) => (id: string): Folder | undefined => {
      return state.folders.find((folder: Folder) => folder.id === id);
    },
    getFilesByType: (state) => (mimeType: string): FileItem[] => {
      return state.files.filter((file: FileItem) => file.mimeType.startsWith(mimeType));
    },
    getTotalSize: (state): number => {
      return state.files.reduce((total: number, file: FileItem) => total + file.size, 0);
    },
    getFileCount: (state): number => {
      return state.files.length
    },
    getFolderCount: (state): number => {
      return state.folders.length
    },
    // Helper to get files in current folder (including uploading files)
    getFilesInCurrentFolder: (state): (FileItem | UploadingFile)[] => {
      const regularFiles = state.files.filter(file => file.folderId === state.currentFolderId);
      const uploadingFiles = state.uploadingFiles.filter(file => file.folderId === state.currentFolderId);
      return [...regularFiles, ...uploadingFiles];
    },
    // Helper to get subfolders in current folder
    getSubfoldersInCurrentFolder: (state): Folder[] => {
      return state.folders.filter(folder => folder.parentId === state.currentFolderId);
    },
    // Add temporary getter for downloads until proper download management is implemented
    allDownloads: (): any[] => {
      return []
    }
  },

  actions: {
    // Fetch app structure (replaces fetchFiles and fetchStructure)
    async fetchAppStructure(appId: string): Promise<void> {
      this.loading = true
      this.error = null

      try {
        const structure = await apiService.getAppStructure(appId)
        
        // Flatten the hierarchical structure into separate arrays
        this.files = []
        this.folders = []
        
        // Add root level files
        this.files.push(...structure.files)
        
        // Recursively flatten folders and their files
        const flattenFolders = (folders: any[], parentLevel = 0) => {
          folders.forEach(folder => {
            this.folders.push({
              id: folder.id,
              name: folder.name,
              appId: folder.appId,
              parentId: folder.parentId,
              createdAt: folder.createdAt,
              updatedAt: folder.updatedAt
            })
            
            // Add files from this folder
            this.files.push(...folder.files)
            
            // Recursively process subfolders
            if (folder.folders && folder.folders.length > 0) {
              flattenFolders(folder.folders, parentLevel + 1)
            }
          })
        }
        
        flattenFolders(structure.folders)
        
        // Sort for consistent display
        this.files.sort((a, b) => a.name.localeCompare(b.name))
        this.folders.sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to fetch app structure'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Fetch files for an app (backward compatibility)
    async fetchFiles(appId: string): Promise<void> {
      return this.fetchAppStructure(appId)
    },

    // Create a new folder
    async createFolder(appId: string, folderData: { name: string; parentId?: string }): Promise<Folder> {
      try {
        const folder = await apiService.createFolder(appId, {
          name: folderData.name,
          parentId: folderData.parentId || this.currentFolderId || undefined
        })
        
        // Add to folders array
        this.folders.push(folder)
        this.folders.sort((a, b) => a.name.localeCompare(b.name))
        
        return folder
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to create folder'
        throw error
      }
    },

    // Rename a folder
    async renameFolder(appId: string, folderId: string, name: string): Promise<Folder> {
      try {
        const updatedFolder = await apiService.renameFolder(appId, folderId, name)
        
        // Update in folders array
        const index = this.folders.findIndex(f => f.id === folderId)
        if (index !== -1) {
          this.folders[index] = updatedFolder
          this.folders.sort((a, b) => a.name.localeCompare(b.name))
        }
        
        return updatedFolder
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to rename folder'
        throw error
      }
    },

    // Delete a folder
    async deleteFolder(appId: string, folderId: string): Promise<void> {
      try {
        await apiService.deleteFolder(appId, folderId)
        
        // Remove from folders array
        this.folders = this.folders.filter(f => f.id !== folderId)
        // Also remove any files that were in the deleted folder
        this.files = this.files.filter(f => f.folderId !== folderId)
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to delete folder'
        throw error
      }
    },

    // Rename a file
    async renameFile(appId: string, fileId: string, name: string): Promise<FileItem> {
      try {
        const updatedFile = await apiService.renameFile(appId, fileId, name)
        
        // Update in files array
        const index = this.files.findIndex(f => f.id === fileId)
        if (index !== -1) {
          this.files[index] = updatedFile
          this.files.sort((a, b) => a.name.localeCompare(b.name))
        }
        
        return updatedFile
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to rename file'
        throw error
      }
    },

    // Upload file with chunked upload
    async uploadFile(
      file: File, 
      appId: string, 
      options: Partial<ChunkedUploadOptions> = {}
    ): Promise<FileItem> {
      const uploadId = `${file.name}-${Date.now()}`
      const folderId = options.folderId || this.currentFolderId || null
      
      try {
        // Add to uploading files immediately
        this.addUploadingFile(file, uploadId, appId, folderId)
        
        // Set up progress tracking
        const uploadOptions: ChunkedUploadOptions = {
          ...options,
          folderId: folderId || undefined,
          onProgress: (progress: UploadProgress) => {
            this.uploadProgress[uploadId] = progress
            this.updateUploadingFileProgress(uploadId, progress.percentage)
            if (options.onProgress) {
              options.onProgress(progress)
            }
          },
          onError: (error: ApiError) => {
            this.error = error.error
            delete this.uploadProgress[uploadId]
            this.removeUploadingFile(uploadId, error.error)
            if (options.onError) {
              options.onError(error)
            }
          }
        }

        const uploadedFile = await ChunkedUploadService.uploadFile(file, appId, uploadOptions)
        
        // Complete the upload - remove from uploading files and add to regular files
        this.completeUploadingFile(uploadId, uploadedFile)
        
        // Clean up progress
        delete this.uploadProgress[uploadId]
        
        return uploadedFile
      } catch (error) {
        delete this.uploadProgress[uploadId]
        this.removeUploadingFile(uploadId, (error as ApiError).error || 'Upload failed')
        this.error = (error as ApiError).error || 'Upload failed'
        throw error
      }
    },

    // Upload multiple files
    async uploadFiles(
      files: File[], 
      appId: string, 
      options: Partial<ChunkedUploadOptions> = {}
    ): Promise<FileItem[]> {
      const uploadedFiles: FileItem[] = []
      
      for (const file of files) {
        try {
          const uploadedFile = await this.uploadFile(file, appId, options)
          uploadedFiles.push(uploadedFile)
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error)
          // Continue with other files
        }
      }
      
      return uploadedFiles
    },

    // Get download URL for file
    async getDownloadUrl(
      appId: string, 
      fileId: string, 
      options?: DownloadRequest
    ): Promise<DownloadResponse> {
      try {
        return await apiService.getDownloadUrl(appId, fileId, options)
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to get download URL'
        throw error
      }
    },

    // Download file
    async downloadFile(
      appId: string, 
      fileId: string, 
      options?: DownloadRequest
    ): Promise<Blob> {
      try {
        const downloadResponse = await this.getDownloadUrl(appId, fileId, options)
        
        // Add Range header if provided for chunked downloads
        const headers: Record<string, string> = {}
        if (downloadResponse.rangeHeader) {
          headers['Range'] = downloadResponse.rangeHeader
        }
        
        return await apiService.downloadFile(downloadResponse.url, headers)
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to download file'
        throw error
      }
    },

    // Download file with progress tracking
    async downloadFileWithProgress(
      appId: string,
      fileId: string,
      onProgress?: (loaded: number, total: number) => void,
      options?: DownloadRequest
    ): Promise<Blob> {
      try {
        // Track download progress
        if (onProgress) {
          this.downloadProgress[fileId] = { loaded: 0, total: 0 }
        }

        const downloadResponse = await this.getDownloadUrl(appId, fileId, options)
        
        // Add Range header if provided for chunked downloads
        const headers: Record<string, string> = {}
        if (downloadResponse.rangeHeader) {
          headers['Range'] = downloadResponse.rangeHeader
        }
        
        // Use fetch for progress tracking
        const response = await fetch(downloadResponse.url, { headers })
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const contentLength = response.headers.get('content-length')
        const total = contentLength ? parseInt(contentLength, 10) : 0
        
        if (!response.body) {
          throw new Error('Response body is null')
        }

        const reader = response.body.getReader()
        const chunks: Uint8Array[] = []
        let loaded = 0

        while (true) {
          const { done, value } = await reader.read()
          
          if (done) break
          
          chunks.push(value)
          loaded += value.length
          
          if (onProgress && total > 0) {
            this.downloadProgress[fileId] = { loaded, total }
            onProgress(loaded, total)
          }
        }

        // Clean up progress tracking
        delete this.downloadProgress[fileId]
        
        // Combine chunks into a single blob
        return new Blob(chunks)
      } catch (error) {
        delete this.downloadProgress[fileId]
        this.error = (error as ApiError).error || 'Failed to download file'
        throw error
      }
    },

    // Delete file
    async deleteFile(appId: string, fileId: string): Promise<void> {
      try {
        await apiService.deleteFile(appId, fileId)
        
        // Remove from files array
        this.files = this.files.filter(f => f.id !== fileId)
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to delete file'
        throw error
      }
    },

    // Set current file
    setCurrentFile(file: FileItem | null): void {
      this.currentFile = file
    },

    // Set current file by ID
    setCurrentFileById(fileId: string): void {
      const file = this.getFileById(fileId)
      this.currentFile = file || null
    },

    // Set current folder
    setCurrentFolder(folderId: string | null): void {
      this.currentFolderId = folderId
    },

    // Clear all data
    clearFiles(): void {
      this.files = []
      this.folders = []
      this.uploadingFiles = []
      this.currentFile = null
      this.currentFolderId = null
    },

    // Clear error
    clearError(): void {
      this.error = null
    },

    // Add uploading file to show in main list
    addUploadingFile(file: File, uploadId: string, appId: string, folderId: string | null = null): void {
      const uploadingFile: UploadingFile = {
        id: `uploading-${uploadId}`,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        folderId: folderId || this.currentFolderId,
        appId,
        uploadId,
        progress: 0,
        status: 'uploading',
        createdAt: new Date().toISOString()
      }
      
      this.uploadingFiles.push(uploadingFile)
    },

    // Update uploading file progress
    updateUploadingFileProgress(uploadId: string, progress: number): void {
      const uploadingFile = this.uploadingFiles.find(f => f.uploadId === uploadId)
      if (uploadingFile) {
        uploadingFile.progress = progress
        uploadingFile.status = progress === 100 ? 'processing' : 'uploading'
      }
    },

    // Remove uploading file and add to regular files
    completeUploadingFile(uploadId: string, completedFile: FileItem): void {
      // Remove from uploading files
      this.uploadingFiles = this.uploadingFiles.filter(f => f.uploadId !== uploadId)
      
      // Add to regular files
      this.files.unshift(completedFile)
    },

    // Remove uploading file on error
    removeUploadingFile(uploadId: string, error?: string): void {
      const uploadingFile = this.uploadingFiles.find(f => f.uploadId === uploadId)
      if (uploadingFile) {
        if (error) {
          uploadingFile.status = 'error'
          uploadingFile.error = error
        } else {
          this.uploadingFiles = this.uploadingFiles.filter(f => f.uploadId !== uploadId)
        }
      }
    },

    // Initialize store
    async initialize(): Promise<void> {
      // Any initialization logic
    },

    // Download management placeholders
    async resumeDownload(downloadId: string): Promise<void> {
      // TODO: Implement download resume
    },

    async pauseDownload(downloadId: string): Promise<void> {
      // TODO: Implement download pause
    },

    async cancelDownload(downloadId: string): Promise<void> {
      // TODO: Implement download cancel
    },

    // Save file to device
    saveFile(blob: Blob, filename: string): void {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    // Utility methods
    formatFileSize(bytes: number): string {
      if (bytes === 0) return '0 Bytes'
      
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    getFileTypeCategory(mimeType: string): string {
      if (mimeType.startsWith('image/')) return 'image'
      if (mimeType.startsWith('video/')) return 'video'
      if (mimeType.startsWith('audio/')) return 'audio'
      if (mimeType.startsWith('text/')) return 'text'
      if (mimeType === 'application/pdf') return 'pdf'
      return 'other'
    }
  }
}) 

export type { UploadingFile }